import React, { useContext } from 'react';
import { PrioritizationMetric } from '@scale/shared-types';
import { SelectMenu } from '@scale/scaleui';
import AutocompleteInput from 'react-scale/corp/components/QM/pipeline/PrioritizationInputs/AutocompleteInput';
import NumberArrayInput from 'react-scale/corp/components/QM/pipeline/PrioritizationInputs/NumberArrayInput';
import NumberInput from 'react-scale/corp/components/QM/pipeline/PrioritizationInputs/NumberInput';
import StringArrayInput from 'react-scale/corp/components/QM/pipeline/PrioritizationInputs/StringArrayInput';
import { ProjectBatchesContext } from 'react-scale/corp/components/QM/pipeline/prioritization/context';
import { isEmpty as _isEmpty } from 'lodash';

const AverageHandlingTimeMinutesConstantInput: React.FC<{
  value: number | undefined;
  setValue: (value: number | undefined) => void;
}> = ({ value, setValue }) => {
  return <NumberInput value={value} setValue={setValue} min={0} step={1} />;
};

const BatchIdConstantInput: React.FC<{
  value: string[] | undefined;
  setValue(value: string[] | undefined): void;
}> = ({ value, setValue }) => {
  const projectBatches = useContext(ProjectBatchesContext);
  const [displayValue, setDisplayValue] = React.useState<string[]>(value ?? []);
  const handleChange = (value: string[]) => {
    if (_isEmpty(value)) {
      setValue(undefined);
      return;
    }
    setValue(value);
    setDisplayValue(value);
  };

  return (
    <AutocompleteInput
      values={displayValue}
      options={projectBatches.map(({ _id }) => _id)}
      setSelectedOptions={value => {
        handleChange(value);
      }}
      canCreateOption={false}
      trailingIcon={'caret-down'}
      inputProps={{
        placeholder: 'Select or type...',
      }}
      getLabelForOption={id => {
        const batchName = projectBatches.find(({ _id }) => _id === id)?.name;
        return `${batchName}: ${id}`;
      }}
    />
  );
};

const IsSuperAttemptConstantInput: React.FC<{
  value: boolean | undefined;
  setValue: (value: boolean | undefined) => void;
}> = ({ value, setValue }) => {
  return (
    <SelectMenu
      selectedOptionId={String(value)}
      setSelected={value => setValue(value === 'true')}
      options={[
        { id: 'true', name: 'true' },
        { id: 'false', name: 'false' },
      ]}
    />
  );
};

const IsSuperAttempterConstantInput: React.FC<{
  value: boolean | undefined;
  setValue: (value: boolean | undefined) => void;
}> = ({ value, setValue }) => {
  return (
    <SelectMenu
      selectedOptionId={String(value)}
      setSelected={value => setValue(value === 'true')}
      options={[
        { id: 'true', name: 'true' },
        { id: 'false', name: 'false' },
      ]}
    />
  );
};

const NumReviewedAttemptsConstantInput: React.FC<{
  value: number | undefined;
  setValue: (value: number | undefined) => void;
}> = ({ value, setValue }) => {
  return <NumberInput min={0} value={value} setValue={setValue} step={1} />;
};

const ReviewedAttemptQMSScoresConstantInput: React.FC<{
  value: number[] | undefined;
  setValue: (value: number[] | undefined) => void;
}> = ({ value, setValue }) => {
  return (
    <NumberArrayInput
      value={value}
      setValue={setValue}
      hint={
        'To represent a list, enter a comma separated list of values, e.g. "value1, value2, value3" without the quotes.'
      }
    />
  );
};

const ReviewedAttemptQMSScoresMinConstantInput: React.FC<{
  value: number | undefined;
  setValue: (value: number | undefined) => void;
}> = ({ value, setValue }) => {
  return <NumberInput value={value} setValue={setValue} min={0} max={5} step={0.5} />;
};

const ReviewedAttemptQMSScoresMaxConstantInput: React.FC<{
  value: number | undefined;
  setValue: (value: number | undefined) => void;
}> = ({ value, setValue }) => {
  return <NumberInput value={value} setValue={setValue} min={0} max={5} step={0.5} />;
};

const ReviewedAttemptQMSScoresAverageConstantInput: React.FC<{
  value: number | undefined;
  setValue: (value: number | undefined) => void;
}> = ({ value, setValue }) => {
  return <NumberInput value={value} setValue={setValue} min={0} max={5} step={0.1} />;
};

const SBQRateConstantInput: React.FC<{
  value: number | undefined;
  setValue: (value: number | undefined) => void;
}> = ({ value, setValue }) => {
  return <NumberInput value={value} setValue={setValue} min={0} max={1} step={0.05} />;
};

const NumAttemptsConstantInput: React.FC<{
  value: number | undefined;
  setValue: (value: number | undefined) => void;
}> = ({ value, setValue }) => {
  return <NumberInput value={value} setValue={setValue} min={0} step={1} />;
};

const WorkerSkillsConstantInput: React.FC<{
  value: string[] | undefined;
  setValue: (value: string[] | undefined) => void;
}> = ({ value, setValue }) => {
  return (
    <StringArrayInput
      value={value}
      setValue={setValue}
      hint={
        'To represent a list, enter a comma separated list of values, e.g. "value1, value2, value3" without the quotes.'
      }
    />
  );
};

const WorkerSkillSpecializationsConstantInput: React.FC<{
  value: string[] | undefined;
  setValue: (value: string[] | undefined) => void;
}> = ({ value, setValue }) => {
  return (
    <StringArrayInput
      value={value}
      setValue={setValue}
      hint={
        'To represent a list, enter a comma separated list of values, e.g. "value1, value2, value3" without the quotes.'
      }
    />
  );
};

const WorkerTagsConstantInput: React.FC<{
  value: string[] | undefined;
  setValue: (value: string[] | undefined) => void;
}> = ({ value, setValue }) => {
  return (
    <StringArrayInput
      value={value}
      setValue={setValue}
      hint={
        'To represent a list, enter a comma separated list of values, e.g. "value1, value2, value3" without the quotes.'
      }
    />
  );
};

export const PrioritizationMetricToConstantInputComponentMap: Record<
  PrioritizationMetric,
  React.FC<{ value: any; setValue: (value: any) => void }>
> = {
  [PrioritizationMetric.AverageHandlingTimeMinutes]: AverageHandlingTimeMinutesConstantInput,
  [PrioritizationMetric.BatchId]: BatchIdConstantInput,
  [PrioritizationMetric.IsSuperAttempt]: IsSuperAttemptConstantInput,
  [PrioritizationMetric.IsSuperAttempter]: IsSuperAttempterConstantInput,
  [PrioritizationMetric.NumAttempts]: NumAttemptsConstantInput,
  [PrioritizationMetric.NumReviewedAttempts]: NumReviewedAttemptsConstantInput,
  [PrioritizationMetric.ReviewedAttemptQMSScores]: ReviewedAttemptQMSScoresConstantInput,
  [PrioritizationMetric.ReviewedAttemptQMSScoresMin]: ReviewedAttemptQMSScoresMinConstantInput,
  [PrioritizationMetric.ReviewedAttemptQMSScoresMax]: ReviewedAttemptQMSScoresMaxConstantInput,
  [PrioritizationMetric.ReviewedAttemptQMSScoresAverage]:
    ReviewedAttemptQMSScoresAverageConstantInput,
  [PrioritizationMetric.SBQRate]: SBQRateConstantInput,
  [PrioritizationMetric.WorkerSkills]: WorkerSkillsConstantInput,
  [PrioritizationMetric.WorkerSkillSpecializations]: WorkerSkillSpecializationsConstantInput,
  [PrioritizationMetric.WorkerTags]: WorkerTagsConstantInput,
};
